import * as React from 'react';
import {message, useIntl} from '@sail/intl';
import {useFlag} from '@sail/access';
import {ContentState} from '@stripe-internal/connect-embedded-lib';
import {useAuthContext} from '../../../data-layer-client/AuthContext/AuthContext';
import {useOnboardingContext} from '../../../data-layer-client/OnboardingContext/OnboardingContext';
import type {ConnectElementImportKeys} from '../../../connect/ConnectJSInterface/ConnectElementList';
import {getTestmodeLogger} from '../../../utils/getLogger';
import {FLAGS} from '../../../utils/flags/flags';

export const messages = {
  onboardingDisabledTitle: message({
    id: 'connectEmbed.accountOnboardingContainer.accountNotOnboarded.title',
    defaultMessage: "Couldn't load",
    description:
      'Title message shown to the user indicating that the account is not yet onboarded',
  }),
  onboardingDisabledSubtitle: message({
    id: 'connectEmbed.accountOnboardingContainer.accountNotOnboarded.subtitle',
    defaultMessage: 'Contact us for support.',
    description:
      'Subtitle message shown to the user indicating that the account is not yet onboarded',
  }),
};

const AccountNotOnboardedMessage = () => {
  const intl = useIntl();
  return (
    <ContentState
      title={intl.formatMessage(messages.onboardingDisabledTitle)}
      description={intl.formatMessage(messages.onboardingDisabledSubtitle)}
    />
  );
};

type Props = {
  requiresOnboarding: boolean | undefined;
  connectElement: ConnectElementImportKeys;
};

export const AccountOnboardingContainer: React.FC<Props> = ({
  children,
  requiresOnboarding: requiresOnboardingProp,
  connectElement,
}) => {
  const {authInfo} = useAuthContext();
  const {detailsSubmitted} = useOnboardingContext();
  const enableAccountManagementWithoutOnboarding = useFlag(
    FLAGS.ENABLE_ACCOUNT_MANAGEMENT_WITHOUT_ACCOUNT_SUBMISSION,
  );

  const enableWithoutOnboarding =
    [
      'stripe-connect-account-management',
      'stripe-connect-account-management-form',
    ].includes(connectElement) && enableAccountManagementWithoutOnboarding;
  const requiresOnboarding = !enableWithoutOnboarding && requiresOnboardingProp;

  const satisfiesOnboardingRequirement =
    !requiresOnboarding ||
    !authInfo ||
    detailsSubmitted ||
    authInfo.merchantData?.connectedAccountDetailsSubmitted;

  React.useEffect(() => {
    // We have to check for authInfo as well otherwise this warning will be logged
    // even when the embedded component is rendered.
    // This is because the flags are only instantiated after the auth context is set.
    if (authInfo && !satisfiesOnboardingRequirement) {
      const testmodeLogger = getTestmodeLogger(!authInfo.livemode);
      testmodeLogger.warn(
        `Warning: The ${connectElement} component is not rendered as the Stripe account has not gone through onboarding.`,
      );
    }
  }, [satisfiesOnboardingRequirement, authInfo, connectElement]);

  // If this component does not require onboarding, the account is already onboarded, or
  // the onboarding component is not enabled, or account has standard dashboard access
  // while standard onboarding is disabled,then we simply pass through
  if (satisfiesOnboardingRequirement) {
    return <>{children}</>;
  }

  // If the account is not onboarded, we render an error
  return <AccountNotOnboardedMessage />;
};
